<script lang="ts">
  import { t } from '$core/i18n';
  import { untrack } from 'svelte';
  import { Icon } from '$ui/primitives/Icon';
  import SidebarItem from './SidebarItem.svelte';
  import SidebarSection from './SidebarSection.svelte';
  import SidebarUser from './SidebarUser.svelte';

  export type ShellView =
    | 'session'
    | 'history'
    | 'analytics'
    | 'forecast'
    | 'checklist'
    | 'leaderboard'
    | 'friends'
    | 'groups'
    | 'goals'
    | 'conjectures'
    | 'sleep'
    | 'preferences'
    | 'quarantine';

  let {
    active = 'session',
    onNavigate,
    user,
    onUserClick,
    quarantineCount = 0,
    pendingFriendsCount = 0,
    initialCollapsed = true
  }: {
    active?: ShellView;
    onNavigate: (view: ShellView) => void;
    user: {
      name: string;
      avatarUrl?: string | null;
      syncLabel: string;
      syncVariant?: 'idle' | 'syncing' | 'pending' | 'error';
    };
    onUserClick?: () => void;
    quarantineCount?: number;
    pendingFriendsCount?: number;
    initialCollapsed?: boolean;
  } = $props();

  const nav = (v: ShellView) => () => onNavigate(v);

  let isCollapsed = $state(untrack(() => initialCollapsed));
</script>

<aside class="sidebar" class:collapsed={isCollapsed} aria-label={t('shell.nav.ariaSidebar')}>
  <div class="sidebar-header">
    <div class="brand">
      <span class="brand-icon" aria-hidden="true">
        <Icon name="activity" size={20} />
      </span>
      {#if !isCollapsed}
        <span class="brand-name">{t('shell.brand')}</span>
      {/if}
    </div>
  </div>

  <button class="toggle-btn" onclick={() => isCollapsed = !isCollapsed} aria-label="Toggle sidebar">
    <Icon name={isCollapsed ? 'chevron-right' : 'chevron-left'} size={16} />
  </button>

  <nav class="nav">
    <SidebarSection label={t('shell.nav.studySection')} collapsed={isCollapsed}>
      <SidebarItem
        icon="play"
        label={t('shell.nav.session')}
        active={active === 'session'}
        onclick={nav('session')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="history"
        label={t('shell.nav.history')}
        active={active === 'history'}
        onclick={nav('history')}
        collapsed={isCollapsed}
      />
    </SidebarSection>

    <SidebarSection label={t('shell.nav.progressSection')} collapsed={isCollapsed}>
      <SidebarItem
        icon="bar-chart-3"
        label={t('shell.nav.analytics')}
        active={active === 'analytics'}
        onclick={nav('analytics')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="calendar-days"
        label={t('shell.nav.forecast')}
        active={active === 'forecast'}
        onclick={nav('forecast')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="list-checks"
        label="Syllabus"
        active={active === 'checklist'}
        onclick={nav('checklist')}
        collapsed={isCollapsed}
      />
    </SidebarSection>

    <SidebarSection label={t('shell.nav.communitySection')} collapsed={isCollapsed}>
      <SidebarItem
        icon="trophy"
        label={t('shell.nav.leaderboard')}
        active={active === 'leaderboard'}
        onclick={nav('leaderboard')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="users"
        label={t('shell.nav.friends')}
        active={active === 'friends'}
        badge={pendingFriendsCount}
        onclick={nav('friends')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="users-round"
        label={t('shell.nav.groups')}
        active={active === 'groups'}
        onclick={nav('groups')}
        collapsed={isCollapsed}
      />
    </SidebarSection>

    <SidebarSection label={t('shell.nav.personalSection')} collapsed={isCollapsed}>
      <SidebarItem
        icon="target"
        label={t('shell.nav.goals')}
        active={active === 'goals'}
        onclick={nav('goals')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="lightbulb"
        label={t('shell.nav.conjectures')}
        active={active === 'conjectures'}
        onclick={nav('conjectures')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="moon"
        label={t('shell.nav.sleep')}
        active={active === 'sleep'}
        onclick={nav('sleep')}
        collapsed={isCollapsed}
      />
    </SidebarSection>

    <SidebarSection label={t('shell.nav.systemSection')} collapsed={isCollapsed}>
      <SidebarItem
        icon="settings"
        label={t('shell.nav.preferences')}
        active={active === 'preferences'}
        onclick={nav('preferences')}
        collapsed={isCollapsed}
      />
      <SidebarItem
        icon="shield-alert"
        label={t('shell.nav.quarantine')}
        active={active === 'quarantine'}
        badge={quarantineCount}
        onclick={nav('quarantine')}
        collapsed={isCollapsed}
      />
    </SidebarSection>
  </nav>

  <div class="bottom">
    <SidebarUser
      name={user.name}
      avatarUrl={user.avatarUrl}
      syncLabel={user.syncLabel}
      syncVariant={user.syncVariant}
      onclick={onUserClick ?? nav('preferences')}
      collapsed={isCollapsed}
    />
  </div>
</aside>

<style>
  /* Réplica del sidebar del IAGIT.
     Fuente: /studyquantifierIAGIT/css/layout.css + componentes.css */

  .sidebar {
    display: flex;
    flex-direction: column;
    width: var(--sidebar-width);
    height: 100dvh;
    background: var(--color-surface);
    border-right: var(--border-width) solid var(--color-border);
    position: sticky;
    top: 0;
    flex: 0 0 auto;
    padding-top: var(--space-4);
    overflow: visible;
    transition: width 200ms var(--ease-out);
    z-index: 20;
  }

  .sidebar.collapsed {
    width: var(--sidebar-width-collapsed);
  }

  /* ---------- HEADER (brand + email) ---------- */
  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--space-4) var(--space-4);
    border-bottom: var(--border-width) solid var(--color-border);
    margin-bottom: var(--space-4);
    flex: 0 0 auto;
  }

  .sidebar.collapsed .sidebar-header {
    justify-content: center;
    padding: 0 0 var(--space-4) 0;
  }

  .toggle-btn {
    position: absolute;
    top: 16px;
    right: 0;
    transform: translateX(50%);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-text-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    z-index: 10;
    transition: all 200ms var(--ease-out);
  }

  .toggle-btn:hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
    border-color: var(--color-accent);
    transform: translateX(50%) scale(1.15);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .brand-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: var(--radius-sm);
    background: var(--color-accent-tint);
    color: var(--color-accent);
    flex: 0 0 auto;
  }

  .brand-name {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ---------- NAV (scroll area con stagger) ---------- */
  .nav {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 var(--space-4);
    display: flex;
    flex-direction: column;
    scrollbar-width: thin;
  }

  .sidebar.collapsed .nav {
    padding: 0 var(--space-2);
  }

  /* Stagger: cada SidebarSection entra escalonada al cargar.
     El easing es out-gentle (0.16, 1, 0.3, 1) que ya es --ease-out. */
  .nav :global(> section) {
    animation: sidebar-section-in 600ms var(--ease-out) both;
  }
  .nav :global(> section:nth-child(1)) { animation-delay: 0ms; }
  .nav :global(> section:nth-child(2)) { animation-delay: 60ms; }
  .nav :global(> section:nth-child(3)) { animation-delay: 120ms; }
  .nav :global(> section:nth-child(4)) { animation-delay: 180ms; }
  .nav :global(> section:nth-child(5)) { animation-delay: 240ms; }

  @keyframes sidebar-section-in {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  /* ---------- FOOTER ---------- */
  .bottom {
    flex: 0 0 auto;
    background: var(--color-surface);
    border-top: var(--border-width) solid var(--color-border);
  }

  @media (prefers-reduced-motion: reduce) {
    .nav :global(> section) {
      animation: none;
    }
  }
</style>